import ApiEndpoints from "@app/utils/ApiEndpoints";
import axios from "@app/Security/AxiosAuthentication";
import { AxiosRequestConfig, AxiosError } from "axios";
import RollbarHelper from "@app/utils/RollbarHelper";

export default class TwoFactorHelper {

    /**
     * Sends the two factor code to the users preferred 
     * option; email or text message.
     * 
     * @param controller  AbortController - handles if component unmounts or if cancel button is added
     * @param newType     Optional 2FA type to override the user's current type (used to test a 2FA
     *                    settings before allowing the user to save)
     * @param newDelivery Optional 2FA delivery destination (email, phone number) to override the user's
     *                    current delivery (used to test new 2FA settings before allowing the user to save)
     * 
     * @returns [boolean, string] A tuple with a boolean indicating if the code sent successfully,
     *                            and an error message if it didn't
     */
    static sendTwoFactorCode = async (controller: AbortController, newType?: string, newDelivery?: string): Promise<[boolean, string]> => {
        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.TWO_FACTOR_SEND;

        const genericErrorMessage = 'An error occurred sending the code, try the resend link or contact support.';

        let url = baseUrl + endPoint;

        // Add optional params for new type and delivery
        const params = new URLSearchParams();

        if (newType) {
            params.append('new_2fa_type', newType);
        }

        if (newDelivery) {
            params.append('new_2fa_delivery', newDelivery);
        }

        if (newType || newDelivery) {
            url = `${url}?${params.toString()}`;
        }

        const options: AxiosRequestConfig<never> = {
            withCredentials: true,
            signal: controller.signal            
        };

        try {

            const response = await axios.get<{message: string}>(url, options);

            if (response.status === 200) {
                return [true, ''];
            } else {
                return [false, response.data.message ?? genericErrorMessage];
            }

        } catch (error) {
            RollbarHelper.logRequestError(error, "sendTwoFactorCode()");

            let errorMessage = genericErrorMessage;
            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<{message: string}>;
                errorMessage = axiosError?.response?.data?.message ?? genericErrorMessage
            }

            return [false, errorMessage];
        }

    }

    /**
     * Validate the 2FA code 
     * @param codeToValidate 
     * @param newType     Optional 2FA type to override the user's current type (used to test a 2FA
     *                    settings before allowing the user to save)
     * @param newDelivery Optional 2FA delivery destination (email, phone number) to override the user's
     *                    current delivery (used to test new 2FA settings before allowing the user to save)
     * 
     * @returns [boolean, string] A tuple with a boolean indicating if the code was verified
     *                            successfully, and an error message if it didn't
     */
    static verifyCode = async (codeToValidate: string, newType?: string, newDelivery?: string): Promise<[boolean, string]> => {
        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.TWO_FACTOR_VERIFY;

        const genericErrorMessage = 'An error occurred attempting to process the code. Please check that you have a valid code and try again or try resending the code.';

        let url = baseUrl + endPoint;

        // Add optional params for new type and delivery
        const params = new URLSearchParams();

        if (newType) {
            params.append('new_2fa_type', newType);
        }

        if (newDelivery) {
            params.append('new_2fa_delivery', newDelivery);
        }

        if (newType || newDelivery) {
            url = `${url}?${params.toString()}`;
        }

        const data = {
            code: codeToValidate,
        };

        const options = {
            withCredentials: true
        };

        try {

            const response = await axios.post<{code?: string, message? : string}>(url, data, options);

            if (response.status === 200) {
                return [true, ''];
            } else {
                return [false, response.data.message ?? genericErrorMessage];
            }

        } catch (error) {

            RollbarHelper.logRequestError(error, "verifyCode()");

            let errorMessage = genericErrorMessage;
            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<{message: string}>;
                errorMessage = axiosError?.response?.data?.message ?? genericErrorMessage
            }

            return [false, errorMessage];

        }

    }

    /**
     * Sends the two factor code to the specified email or phone number during the registration process,
     * after validating the user's registration JWT.
     * 
     * @param controller AbortController - handles if component unmounts or if cancel button is added
     * @param userId     The id of the user registering
     * @param jwt        A valid JWT for the registering user
     * @param type       2FA type, used to test new 2FA settings before allowing the user to save
     * @param delivery   2FA delivery destination (email, phone number), used to test new 2FA settings
     *                   before allowing the user to save
     * 
     * @returns [boolean, string] A tuple with a boolean indicating if the code sent successfully,
     *                            and an error message if it didn't
     */
     static registrationSendTwoFactorCode = async (
        controller: AbortController,
        userId: number,
        jwt: string,
        type: string,
        delivery: string
     ): Promise<[boolean, string]> => {
        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.REGISTRATION_TWO_FACTOR_SEND;

        const genericErrorMessage = 'An error occurred sending the code, try the resend link or contact support.';

        const url = baseUrl + endPoint;

        // Add URL params
        const params = new URLSearchParams();
        params.append('user_id', `${userId}`);
        params.append('jwt', jwt);
        params.append('2fa_type', type);
        params.append('2fa_delivery', delivery);

        const options: AxiosRequestConfig<never> = {
            withCredentials: true,
            signal: controller.signal            
        };

        try {

            const response = await axios.post<{message?: string}>(`${url}?${params.toString()}`, {}, options);

            if (response.status === 200) {
                return [true, ''];
            } else {
                return [false, response.data.message ?? genericErrorMessage];
            }

        } catch (error) {
            RollbarHelper.logRequestError(error, "sendTwoFactorCode()");

            let errorMessage = genericErrorMessage;
            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<{message: string}>;
                errorMessage = axiosError?.response?.data?.message ?? genericErrorMessage
            }

            return [false, errorMessage];
        }

    }

    /**
     * Validate the 2FA code 
     * @param codeToValidate 
     * @param userId     The id of the user registering
     * @param jwt        A valid JWT for the registering user
     * @param type       2FA type, used to test new 2FA settings before allowing the user to save
     * @param delivery   2FA delivery destination (email, phone number), used to test new 2FA settings
     *                   before allowing the user to save
     * 
     * @returns [boolean, string] A tuple with a boolean indicating if the code was verified
     *                            successfully, and an error message if it didn't
     */
     static RegistrationVerifyCode = async (
        codeToValidate: string,
        userId: number,
        jwt: string,
        type: string,
        delivery: string
     ): Promise<[boolean, string]> => {
        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.REGISTRATION_TWO_FACTOR_VERIFY;

        const genericErrorMessage = 'An error occurred attempting to process the code. Please check that you have a valid code and try again or try resending the code.';

        const url = baseUrl + endPoint;

        // Add URL params
        const params = new URLSearchParams();
        params.append('user_id', `${userId}`);
        params.append('jwt', jwt);
        params.append('2fa_type', type);
        params.append('2fa_delivery', delivery);

        const data = {
            code: codeToValidate,
        };

        const options = {
            withCredentials: true
        };

        try {

            const response = await axios.post<{code?: string, message? : string}>(`${url}?${params.toString()}`, data, options);

            if (response.status === 200) {
                return [true, ''];
            } else {
                return [false, response.data.message ?? genericErrorMessage];
            }

        } catch (error) {

            RollbarHelper.logRequestError(error, "verifyCode()");

            let errorMessage = genericErrorMessage;
            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<{message: string}>;
                errorMessage = axiosError?.response?.data?.message ?? genericErrorMessage
            }

            return [false, errorMessage];

        }

    }

}